// Bottom-right notifications on every page: templates being generated (click to see the
// progress) and templates that are ready or failed. An admin's batch shows as one notification
// (how many templates are left), not one per template.

import React from "react";

const RUNNING = ["queued", "planning", "planned", "building"];

/**
 * How far a batch is, 0-100: every template counts with its own progress (the AI writing its
 * scenes, then the scenes being built), so the bar moves all the time, not only when one is done.
 */
export function batchProgress(jobs) {
  if (!jobs.length) return 0;
  const sum = jobs.reduce((s, j) => s + (RUNNING.includes(j.status) ? Math.min(99, Math.max(0, Number(j.progress) || 0)) : 100), 0);
  return Math.round(sum / jobs.length);
}

const thumbUrl = (api, path) => (path ? String(api).replace(/\/$/, "") + "/" + String(path).replace(/^\//, "") : null);

function statusText(job) {
  if (job.status === "queued") return job.queueAhead > 0 ? `Waiting in line (${job.queueAhead} ahead)` : "Starting...";
  if (job.status === "planning") return "Writing your scenes...";
  if (job.step === "save") return "Saving...";
  return "Designing the scenes...";
}

export default function AIToasts({ toasts, jobs, panelId, apiBaseURL, onDismiss, onOpenPanel, onOpenProject, onOpenBatches }) {
  const running = jobs.filter((j) => RUNNING.includes(j.status) && j.id !== panelId && !j.batchId);
  const batches = {};
  jobs.forEach((j) => {
    if (!j.batchId) return;
    const b = (batches[j.batchId] = batches[j.batchId] || { id: j.batchId, left: 0, jobs: [] });
    b.jobs.push(j);
    if (RUNNING.includes(j.status)) b.left += 1;
  });
  const runningBatches = Object.values(batches)
    .filter((b) => b.left > 0)
    .map((b) => ({ ...b, pct: batchProgress(b.jobs) }));
  const shown = toasts.map((t) => ({ ...t, job: jobs.find((j) => j.id === t.jobId) })).filter((t) => t.job);
  if (!running.length && !shown.length && !runningBatches.length) return null;

  return (
    <div className="ais-toasts">
      {runningBatches.map((b) => (
        <button key={"b" + b.id} className="ais-toast ais-toast-running" onClick={onOpenBatches}>
          <span className="ais-toast-icon"><span className="material-icons ais-spin-slow">auto_awesome</span></span>
          <span className="ais-toast-text">
            <strong>Making templates</strong>
            <small>{b.left === 1 ? "1 template left" : `${b.left} templates left`} · {b.pct}%</small>
            <span className="ais-mini-bar"><span style={{ width: Math.max(4, b.pct) + "%" }} /></span>
          </span>
        </button>
      ))}
      {running.map((job) => (
        <button key={"r" + job.id} className="ais-toast ais-toast-running" onClick={() => onOpenPanel(job.id)}>
          <span className="ais-toast-icon"><span className="material-icons ais-spin-slow">auto_awesome</span></span>
          <span className="ais-toast-text">
            <strong>{job.keyword}</strong>
            <small>{statusText(job)}</small>
            <span className="ais-mini-bar"><span style={{ width: Math.max(4, job.progress || 4) + "%" }} /></span>
          </span>
        </button>
      ))}
      {shown.map(({ job, kind }) => {
        if (kind === "batch") {
          return (
            <div key={"t" + job.id} className="ais-toast ais-toast-done">
              <span className="ais-toast-icon"><span className="material-icons">check_circle</span></span>
              <span className="ais-toast-text">
                <strong>Your batch is finished</strong>
                <small>All its templates are done.</small>
                <span className="ais-toast-actions">
                  <button className="ais-link" onClick={onOpenBatches}>Show templates</button>
                </span>
              </span>
              <button className="ais-toast-x" onClick={() => onDismiss(job.id)} title="Dismiss">
                <span className="material-icons">close</span>
              </button>
            </div>
          );
        }
        const img = (job.previews && job.previews[0]) || thumbUrl(apiBaseURL, job.previewImagePath);
        return (
          <div key={"t" + job.id} className={"ais-toast " + (kind === "done" ? "ais-toast-done" : "ais-toast-failed")}>
            {kind === "done" && img ? (
              <img className="ais-toast-thumb" src={img} alt="" />
            ) : (
              <span className="ais-toast-icon">
                <span className="material-icons">{kind === "done" ? "check_circle" : "error_outline"}</span>
              </span>
            )}
            <span className="ais-toast-text">
              <strong>{kind === "done" ? "Your AI template is ready" : "Template not created"}</strong>
              <small>{kind === "done" ? job.projectName || job.keyword : job.error || job.keyword}</small>
              <span className="ais-toast-actions">
                {kind === "done" ? (
                  <button className="ais-link" onClick={() => onOpenProject(job)}>Open in editor</button>
                ) : (
                  <button className="ais-link" onClick={() => onOpenPanel(job.id)}>Details</button>
                )}
              </span>
            </span>
            <button className="ais-toast-x" onClick={() => onDismiss(job.id)} title="Dismiss">
              <span className="material-icons">close</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
